import React from 'react';

interface TableSkeletonProps {
    /** How many placeholder rows to draw. */
    rows?: number;
    /** How many placeholder cells per row. */
    columns?: number;
}

const bar = 'h-3.5 rounded bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 ' +
    'bg-[length:800px_100%] animate-shimmer';

/**
 * Stands in for a table while its data loads, so the page never flashes
 * an empty "No data" state on the way to showing rows.
 */
const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 6 }) => (
    <div className="animate-fade-in" aria-busy="true" aria-live="polite">
        <div className="flex gap-4 border-b border-line bg-slate-50 px-4 py-4">
            {Array.from({ length: columns }).map((_, i) => (
                <div key={i} className={`${bar} flex-1 max-w-[140px] opacity-70`} />
            ))}
        </div>

        {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="flex gap-4 border-b border-line px-4 py-5 last:border-b-0">
                {Array.from({ length: columns }).map((_, c) => (
                    <div
                        key={c}
                        className={`${bar} flex-1`}
                        style={{
                            // Vary the widths so it reads as content, not a grid of identical bars.
                            maxWidth: [90, 150, 120, 100, 130, 80][(r + c) % 6],
                            animationDelay: `${(r * columns + c) * 45}ms`,
                        }}
                    />
                ))}
            </div>
        ))}
    </div>
);

export default TableSkeleton;
